import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions/more-actions';
import { Popover } from 'module/ui/components/atoms/popover';
import {
  ToolbarButtonIcon,
  ToolbarDropdownTriggerButton,
} from 'module/ui/components/atoms/rich-text-editor/toolbar/components';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import {
  faMarker,
  faTrash,
  faCheck,
  faAlignLeft,
  faLayerGroup,
  faPlay,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { PageType } from 'api/page-template/types';
import { startCase } from 'lodash';
import { useSelectedEmailId } from 'queries/emails';
import { useSelectedMeetingId } from 'queries/meeting';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { EmailPreviewWithList } from 'components/organisms/emails/email-preview-with-list';
import { MeetingPreviewWithList } from 'components/organisms/meetings/meeting-preview-with-list';
import { ParticipantFilter } from './participants-filter/dropdown';
import {
  ConfigurationBar,
  ConfigurationGroup,
  ActionBar,
  ThrashButton,
  DoneButton,
  PreviewButton,
} from './section-styles';

type InputOptions = {
  groupBy: string[];
  generateAs: string[];
  participants: any;
};
type InputDefaultValues = {
  groupBy: string;
  generateAs: string;
  participants: string;
};

type ActionBarConfig = {
  canWrapTrashButton?: boolean;
  canShowPreviewButton?: boolean;
  canShowStreaming?: boolean;
};

interface ConfigurationBarProps {
  prompt: any;
  setPrompt: (prompt: any) => void;
  isActionLoading?: boolean;
  onCancel?: () => void;
  onSubmit: (prompt: any) => void;
  onPreview?: () => void;
  allowResolve: boolean;
  actionBarConfig?: ActionBarConfig;
  validation: {
    isDisabled: boolean;
  };
  defaultValues?: Partial<InputDefaultValues>;
  options?: Partial<InputOptions>;
  getNodeAsPayload?: (isPreviewOpen?: boolean) => object;
  pageType?: string;
  pageTriggerListFilter?: GroupedFilter;
}

const StyledPopover = styled(Popover)`
  &,
  .ant-popover-inner-content {
    width: 100%;
  }
`;

export const SharedConfigurationBar: React.FC<ConfigurationBarProps> = ({
  prompt,
  setPrompt,
  isActionLoading,
  onCancel,
  onSubmit,
  onPreview,
  validation,
  defaultValues,
  options,
  allowResolve,
  actionBarConfig,
  getNodeAsPayload,
  pageType,
  pageTriggerListFilter,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { selectedMeetingId, selectedMeetingName, setSelectedMeeting } =
    useSelectedMeetingId();
  const { selectedEmailId, selectedEmailSubject, setSelectedEmailId } =
    useSelectedEmailId();
  const [showPreview, setShowPreview] = useState(false);

  const handleMeetingClick = (meetingId: number, meetingName: string) => {
    setSelectedMeeting(meetingId, meetingName);
    setShowPreview(true);
    onPreview?.();
  };

  const handleEmailClick = (emailThreadId: number, email: any) => {
    setSelectedEmailId(emailThreadId, email.subject);
    setShowPreview(true);
    onPreview?.();
  };

  // Derived state
  const showList = isPreviewOpen && !showPreview;

  const GroupByDropdownMenuProps = useMemo(() => {
    const subMenuItems = options?.groupBy?.map((option) => ({
      key: option,
      label: startCase(option.toLowerCase()),
    }));
    const commandMenuItems = [
      {
        type: 'group',
        key: '1',
        label: <FormattedMessage id="rich_text_editor.sections.group_by" />,
        icon: <FontAwesomeIcon icon={faAlignLeft} />,
        children: subMenuItems,
      },
    ];

    return {
      selectedKeys: [prompt.groupBy || defaultValues?.groupBy],
      onClick: (e) => setPrompt({ ...prompt, groupBy: e.key }),
      items: commandMenuItems as ItemType[],
    };
  }, [options?.groupBy, prompt, defaultValues?.groupBy, setPrompt]);

  const GenerateAsDropdownMenuProps = useMemo(() => {
    const subMenuItems = options?.generateAs?.map((option) => ({
      key: option,
      label: startCase(option.toLowerCase()),
    }));
    const commandMenuItems = [
      {
        type: 'group',
        key: '2',
        label: <FormattedMessage id="rich_text_editor.sections.generate_as" />,
        icon: <FontAwesomeIcon icon={faAlignLeft} />,
        children: subMenuItems,
      },
    ];

    return {
      selectedKeys: [prompt.generateAs || defaultValues?.generateAs],
      onClick: (e) => setPrompt({ ...prompt, generateAs: e.key }),
      items: commandMenuItems as ItemType[],
    };
  }, [options?.generateAs, prompt, defaultValues?.generateAs, setPrompt]);

  const canWrapTrashButton = actionBarConfig?.canWrapTrashButton ?? false;
  const canShowPreviewButton = actionBarConfig?.canShowPreviewButton ?? false;

  return (
    <>
      <ConfigurationBar hasOptions={!!options}>
        {options && (
          <ConfigurationGroup isDisabled={validation.isDisabled}>
            {options?.groupBy != null && (
              <Dropdown menu={GroupByDropdownMenuProps}>
                <ToolbarDropdownTriggerButton
                  icon={<ToolbarButtonIcon icon={faLayerGroup} />}
                >
                  <FormattedMessage
                    id="rich_text_editor.sections.type"
                    values={{
                      type: startCase(
                        (
                          (prompt.groupBy || defaultValues?.groupBy) ??
                          ''
                        ).toLowerCase(),
                      ),
                    }}
                  />
                </ToolbarDropdownTriggerButton>
              </Dropdown>
            )}
            {options?.generateAs != null && (
              <Dropdown menu={GenerateAsDropdownMenuProps}>
                <ToolbarDropdownTriggerButton
                  icon={<ToolbarButtonIcon icon={faMarker} />}
                >
                  <FormattedMessage
                    id="rich_text_editor.sections.type"
                    values={{
                      type: startCase(
                        (
                          (prompt.generateAs || defaultValues?.generateAs) ??
                          ''
                        ).toLowerCase(),
                      ),
                    }}
                  />
                </ToolbarDropdownTriggerButton>
              </Dropdown>
            )}
            {options?.participants != null && (
              <ParticipantFilter
                participants={prompt.participants}
                defaultValue={defaultValues?.participants}
                options={options?.participants}
                setPrompt={setPrompt}
                prompt={prompt}
              />
            )}
          </ConfigurationGroup>
        )}

        <ActionBar>
          {!canWrapTrashButton && (
            <ThrashButton variant="secondary" onClick={onCancel}>
              <FontAwesomeIcon icon={faTrash} />
            </ThrashButton>
          )}
          {canShowPreviewButton && (
            <StyledPopover
              className="custom-popover"
              open={isPreviewOpen}
              onOpenChange={(visible) => {
                if (!visible) {
                  if (showPreview) {
                    // From preview, go back to meeting list
                    setShowPreview(false);
                  } else {
                    // From meeting list, close popup
                    setIsPreviewOpen(false);
                  }
                } else {
                  setIsPreviewOpen(true);
                }
              }}
              trigger="click"
              placement="right"
              overlayStyle={{
                width: showList ? '460px' : '614px',
              }}
              overlayInnerStyle={{
                padding: 0,
                borderRadius: '8px',
                background: 'var(--white-100)',
              }}
              content={
                pageType === PageType.Email ? (
                  <EmailPreviewWithList
                    showList={showList}
                    showPreview={showPreview}
                    isLoading={false}
                    selectedEmailId={selectedEmailId}
                    selectedEmailSubject={selectedEmailSubject}
                    streaming={actionBarConfig?.canShowStreaming}
                    payload={getNodeAsPayload?.(isPreviewOpen) || {}}
                    isDropdownOpen={isPreviewOpen}
                    pageTriggerListFilter={pageTriggerListFilter}
                    onEmailSelect={handleEmailClick}
                    onBack={() => setShowPreview(false)}
                  />
                ) : (
                  <MeetingPreviewWithList
                    showList={showList}
                    showPreview={showPreview}
                    selectedMeetingId={selectedMeetingId}
                    selectedMeetingName={selectedMeetingName}
                    streaming={actionBarConfig?.canShowStreaming}
                    payload={getNodeAsPayload?.(isPreviewOpen) || {}}
                    pageTriggerListFilter={pageTriggerListFilter}
                    onMeetingSelect={handleMeetingClick}
                    onBack={() => setShowPreview(false)}
                  />
                )
              }
            >
              <Tooltip title={<FormattedMessage id="preview" />}>
                <PreviewButton
                  variant="secondary"
                  onClick={() => {
                    setIsPreviewOpen(true);
                  }}
                  disabled={validation.isDisabled}
                >
                  <FontAwesomeIcon icon={faPlay} />
                </PreviewButton>
              </Tooltip>
            </StyledPopover>
          )}
          {allowResolve ? (
            <DoneButton
              variant="primary"
              onClick={() => onSubmit(prompt)}
              disabled={validation.isDisabled}
            >
              <FontAwesomeIcon icon={faSparkles} />
              {isActionLoading ? (
                <FormattedMessage id="rich_text_editor.sections.generating" />
              ) : (
                <FormattedMessage id="rich_text_editor.sections.generate" />
              )}
            </DoneButton>
          ) : (
            <DoneButton
              variant="primary"
              onClick={() => onSubmit(prompt)}
              disabled={validation.isDisabled}
            >
              <FontAwesomeIcon icon={faCheck} />
              <FormattedMessage id="rich_text_editor.sections.done" />
            </DoneButton>
          )}
          {canWrapTrashButton && (
            <MoreActionsDropdown
              menu={{
                onClick: () => {
                  onCancel?.();
                },
                items: [
                  {
                    type: 'group',
                    label: <FormattedMessage id="more_actions" />,
                    children: [
                      {
                        key: 'delete',
                        label: <FormattedMessage id="delete" />,
                        icon: <FontAwesomeIcon icon={faTrash} />,
                        danger: true,
                      },
                    ],
                  },
                ],
              }}
            />
          )}
        </ActionBar>
      </ConfigurationBar>
    </>
  );
};
